import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  CalculationSharing,
  InsertCalculationSharingBody,
  InsertCalculationSharingResponse,
  UpdateCalculationSharingBody,
} from "./types/calculationSharing";

export const useCalculationSharingApi = () => {
  const api = useApi();

  const getCalculationsUrl = useCallback((isAdmin?: boolean) => `${isAdmin ? "/admin" : ""}/calculations`, []);

  const getBaseUrl = useCallback(
    (calculationId: string, isAdmin?: boolean) => `${getCalculationsUrl(isAdmin)}/${calculationId}/sharing`,
    [getCalculationsUrl],
  );

  // A sharing change also changes the list "shared" flag, and an insert for an unregistered email creates an invite
  const mutateCalculationSharings = useCallback(
    (calculationId: string, isAdmin?: boolean) => {
      mutateCache([
        getCalculationsUrl(isAdmin),
        getBaseUrl(calculationId, isAdmin),
        `${getCalculationsUrl(isAdmin)}/${calculationId}/sharing_invites`,
      ]);
    },
    [getCalculationsUrl, getBaseUrl],
  );

  const useSearchCalculationSharings = useCallback(
    (calculationId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(getBaseUrl(calculationId, isAdmin), query);
      return useSWR<Paginated<CalculationSharing>, CustomError, string>(url, (url) =>
        api.get<Paginated<CalculationSharing>>({ url, signal }),
      );
    },
    [api, getBaseUrl],
  );

  const insertCalculationSharing = useCallback(
    async (
      calculationId: string,
      body: InsertCalculationSharingBody,
      isAdmin?: boolean,
    ): Promise<InsertCalculationSharingResponse> => {
      const result = await api.post<InsertCalculationSharingResponse, InsertCalculationSharingBody>(
        getBaseUrl(calculationId, isAdmin),
        body,
      );
      mutateCalculationSharings(calculationId, isAdmin);
      return result;
    },
    [api, getBaseUrl, mutateCalculationSharings],
  );

  const updateCalculationSharing = useCallback(
    async (
      calculationId: string,
      userId: string,
      body: UpdateCalculationSharingBody,
      isAdmin?: boolean,
    ): Promise<void> => {
      await api.put(`${getBaseUrl(calculationId, isAdmin)}/${userId}`, body);
      mutateCalculationSharings(calculationId, isAdmin);
    },
    [api, getBaseUrl, mutateCalculationSharings],
  );

  const deleteCalculationSharing = useCallback(
    async (calculationId: string, userId: string, isAdmin?: boolean): Promise<void> => {
      await api.del(`${getBaseUrl(calculationId, isAdmin)}/${userId}`);
      mutateCalculationSharings(calculationId, isAdmin);
    },
    [api, getBaseUrl, mutateCalculationSharings],
  );

  return {
    useSearchCalculationSharings,
    insertCalculationSharing,
    updateCalculationSharing,
    deleteCalculationSharing,
  };
};
